import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import {
  Checkbox,
  FormRow,
  IFormOption
} from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  GetGlobalSettingsParamsType,
  IPlatformSetting,
  ISettingsResponse,
  PlatformMaintananceModeRolesType,
  UpdateGlobalSettingParams,
  useGlobalSettingsApi
} from "src/core/shared/api/globalSettings.api";
import type { IStore } from "src/core/shared/store";
import { setAppSettings } from "src/core/shared/store/global/global.actions";
import { PageProps } from "src/pages/Router/routes";

interface IFormFieldValues {
  maintenance_mode_roles: PlatformMaintananceModeRolesType[];
}

/**
 * Maintenance settings page
 *
 * @returns
 */
const MaintenanceSettingsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ globalSettings, globalSettingsApi ] = useGlobalSettingsApi();

  const [ getGlobalSettingsResponse, getGlobalSettings ] = globalSettingsApi<
    ISettingsResponse<"platform", IPlatformSetting>,
    GetGlobalSettingsParamsType
  >("GET_SETTING");

  const [ updateGlobalSettingsResponse, updateGlobalSettings ] = globalSettingsApi<ISettingsResponse<"platform", IPlatformSetting>>("UPDATE_SETTING");
  const [ isLoading, setIsLoading ] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const { currentRole } = useMappedState(({ global }: IStore) => ({ currentRole: global.role }));
  const roles = [ "admin", "customer" ];
  const formValues: IFormFieldValues = { maintenance_mode_roles: globalSettings ? globalSettings.platform.maintenance_mode.roles : [] };

  // On mount
  React.useEffect(() => {
    if (!getGlobalSettingsResponse.data) {
      getGlobalSettings("platform");
    }
  }, []);

  // On getGlobalSettingsResponse
  React.useEffect(() => {
    if (getGlobalSettingsResponse.data && globalSettings) {
      dispatch(setAppSettings({
        ...globalSettings,
        platform: getGlobalSettingsResponse.data.setting
      }));
    }
  }, [ getGlobalSettingsResponse.data ]);

  // On updateGlobalSettingsResponse
  React.useEffect(() => {
    if (updateGlobalSettingsResponse.complete) {
      setIsLoading(false);
    }
  }, [ updateGlobalSettingsResponse.complete ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {globalSettings && (
            <Formik
              initialValues={formValues}
              onSubmit={(values: IFormFieldValues) => {
                setIsLoading(true);

                updateGlobalSettings<UpdateGlobalSettingParams<IPlatformSetting>>({
                  name: "platform",
                  data: { maintenance_mode: { roles: values.maintenance_mode_roles } }
                });
              }}
              render={({
                handleSubmit, values, setFieldValue
              }) => {
                const checkboxOpts: IFormOption[] = _.filter(
                  roles.map(
                    (role): IFormOption => {
                      return {
                        value: role,
                        text: role,
                        checked: _.find(values.maintenance_mode_roles, value => value === role) ? true : false
                      };
                    }
                  ),
                  opt => {
                    if (opt.value === "admin") {
                      if (!currentRole || currentRole.code !== "super-admin") {
                        return false;
                      }
                    }

                    return true;
                  }
                );

                return (
                  <form onSubmit={handleSubmit}>
                    <FormRow>
                      <Text
                        as="p"
                        fontSize="h4"
                      >
                        Maintenance mode enables you to keep users with the selected roles (below) out of the system to give you time to perform your
                        own mainenance or house keeping tasks.
                      </Text>

                      <Checkbox
                        name="maintenance_mode_roles"
                        label="Roles"
                        value={formValues.maintenance_mode_roles}
                        options={checkboxOpts}
                        handleChange={() => {}}
                        handleBlur={() => {}}
                        setFieldValue={setFieldValue}
                      />
                    </FormRow>

                    <FormRow>
                      <Button
                        loading={isLoading}
                        type="submit"
                      >
                        Save
                      </Button>
                    </FormRow>
                  </form>
                );
              }}
            />
          )}
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default MaintenanceSettingsPage;
